import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from '@mui/material';
import { Add as AddIcon, Upload as UploadIcon, CleaningServices as CleanIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import DataTable, { Column } from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  getContacts,
  createContact,
  updateContact,
  deleteContact,
  importContactsFromCsv,
  removeDuplicateContacts,
} from '../../services/api/contentApi';
import { OfficialContact, ContactFormData } from '../../types/content';
import { formatDateTime } from '../../utils/formatters';

const ContactPage: React.FC = () => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [filters, setFilters] = useState<{
    page: number;
    limit: number;
    search?: string;
  }>({
    page: 0,
    limit: 20,
  });
  const [searchInput, setSearchInput] = useState('');
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<OfficialContact | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    itemId: string | null;
  }>({
    open: false,
    itemId: null,
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>({
    defaultValues: {
      organization: '',
      phoneNumber: '',
      affiliation: '',
      department: '',
      position: '',
      name: '',
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['contacts', filters],
    queryFn: () => getContacts(filters),
  });

  const createMutation = useMutation({
    mutationFn: createContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setSnackbar({
        open: true,
        message: '연락처가 추가되었습니다.',
        severity: 'success',
      });
      handleCloseFormDialog();
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || '연락처 추가에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ContactFormData> }) =>
      updateContact(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setSnackbar({
        open: true,
        message: '연락처가 수정되었습니다.',
        severity: 'success',
      });
      handleCloseFormDialog();
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || '연락처 수정에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setSnackbar({
        open: true,
        message: '연락처가 삭제되었습니다.',
        severity: 'success',
      });
      setConfirmDialog({ open: false, itemId: null });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || '연락처 삭제에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const importMutation = useMutation({
    mutationFn: importContactsFromCsv,
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setSnackbar({
        open: true,
        message: `${data.imported}개의 연락처가 일괄 등록되었습니다.`,
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || 'CSV 일괄 등록에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const removeDuplicatesMutation = useMutation({
    mutationFn: removeDuplicateContacts,
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setSnackbar({
        open: true,
        message: `${data.deletedCount}개의 중복 연락처가 삭제되었습니다.`,
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || '중복 제거에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleRowsPerPageChange = (limit: number) => {
    setFilters(prev => ({ ...prev, limit, page: 0 }));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const handleSearchSubmit = () => {
    setFilters(prev => ({
      ...prev,
      search: searchInput || undefined,
      page: 0,
    }));
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleOpenAddDialog = () => {
    setEditingItem(null);
    reset({
      organization: '',
      phoneNumber: '',
      affiliation: '',
      department: '',
      position: '',
      name: '',
    });
    setFormDialogOpen(true);
  };

  const handleOpenEditDialog = (item: OfficialContact) => {
    setEditingItem(item);
    reset({
      organization: item.organization,
      phoneNumber: item.phoneNumber,
      affiliation: item.affiliation || '',
      department: item.department || '',
      position: item.position || '',
      name: item.name || '',
    });
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    setEditingItem(null);
    reset();
  };

  const handleFormSubmit = (data: ContactFormData) => {
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDeleteClick = (id: string) => {
    setConfirmDialog({ open: true, itemId: id });
  };

  const handleConfirmDelete = () => {
    if (confirmDialog.itemId) {
      deleteMutation.mutate(confirmDialog.itemId);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialog({ open: false, itemId: null });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleCsvUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      importMutation.mutate(file);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const columns: Column<OfficialContact>[] = [
    {
      id: 'organization',
      label: '기관명',
      minWidth: 200,
    },
    {
      id: 'phoneNumber',
      label: '전화번호',
      minWidth: 150,
    },
    {
      id: 'affiliation',
      label: '소속',
      minWidth: 120,
      format: (value: string | null) => value || '-',
    },
    {
      id: 'department',
      label: '부서',
      minWidth: 100,
      format: (value: string | null) => value || '-',
    },
    {
      id: 'position',
      label: '직위',
      minWidth: 100,
      format: (value: string | null) => value || '-',
    },
    {
      id: 'name',
      label: '담당자',
      minWidth: 100,
      format: (value: string | null) => value || '-',
    },
    {
      id: 'actions',
      label: '작업',
      minWidth: 150,
      align: 'center',
      format: (_value: any, row: OfficialContact) => (
        <Stack direction="row" spacing={1} justifyContent="center">
          <Button size="small" variant="outlined" onClick={() => handleOpenEditDialog(row)}>
            수정
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => handleDeleteClick(row.id)}
          >
            삭제
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">공식 연락처 관리</Typography>
        <Stack direction="row" spacing={2}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <Button
            variant="outlined"
            color="warning"
            startIcon={<CleanIcon />}
            onClick={() => removeDuplicatesMutation.mutate()}
            disabled={removeDuplicatesMutation.isPending}
          >
            중복 제거
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={handleCsvUploadClick}
            disabled={importMutation.isPending}
          >
            CSV 일괄 등록
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddDialog}>
            연락처 추가
          </Button>
        </Stack>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            label="검색 (기관명, 전화번호, 담당자)"
            value={searchInput}
            onChange={handleSearchChange}
            onKeyPress={handleSearchKeyPress}
            size="small"
            sx={{ minWidth: 300 }}
            placeholder="검색어를 입력하세요"
          />
          <Button variant="contained" onClick={handleSearchSubmit}>
            검색
          </Button>
          {filters.search && (
            <Button
              variant="outlined"
              onClick={() => {
                setSearchInput('');
                setFilters(prev => ({ ...prev, search: undefined, page: 0 }));
              }}
            >
              초기화
            </Button>
          )}
        </Stack>
      </Paper>

      <DataTable
        columns={columns}
        data={data?.items || []}
        total={data?.total || 0}
        page={filters.page}
        rowsPerPage={filters.limit}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        loading={isLoading}
      />

      {/* 추가/수정 폼 다이얼로그 */}
      <Dialog open={formDialogOpen} onClose={handleCloseFormDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? '연락처 수정' : '연락처 추가'}</DialogTitle>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogContent>
            <Stack spacing={2}>
              <Controller
                name="organization"
                control={control}
                rules={{ required: '기관명은 필수입니다' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="기관명"
                    fullWidth
                    error={!!errors.organization}
                    helperText={errors.organization?.message}
                  />
                )}
              />
              <Controller
                name="phoneNumber"
                control={control}
                rules={{ required: '전화번호는 필수입니다' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="전화번호"
                    fullWidth
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber?.message}
                  />
                )}
              />
              <Controller
                name="affiliation"
                control={control}
                render={({ field }) => <TextField {...field} label="소속" fullWidth />}
              />
              <Controller
                name="department"
                control={control}
                render={({ field }) => <TextField {...field} label="부서" fullWidth />}
              />
              <Controller
                name="position"
                control={control}
                render={({ field }) => <TextField {...field} label="직위" fullWidth />}
              />
              <Controller
                name="name"
                control={control}
                render={({ field }) => <TextField {...field} label="담당자명" fullWidth />}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseFormDialog}>취소</Button>
            <Button type="submit" variant="contained">
              {editingItem ? '수정' : '추가'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <ConfirmDialog
        open={confirmDialog.open}
        title="연락처 삭제"
        message="이 연락처를 삭제하시겠습니까?"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmText="삭제"
        confirmColor="error"
      />

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactPage;
